import { useState } from 'react';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import { Button, DisplayFormattedDate, Link } from 'components/common';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';

const POLICIES_UPDATED = '2022-03-28';

const UserConsentUpdate = () => {
  const { dispatch } = useDispatch();
  const { language, messages, serverConfig, user } = useReduxState();

  const [loading, setLoading] = useState(false);

  const confirm = async () => {
    try {
      setLoading(true);
      const consent = {
        ...user.consent,
        changesSeen: new Date().toString()
      };
      const response = await api.post(`/auth/profile`, { consent });
      if (response.data && response.data.success) {
        dispatch(actions.setUser({ ...user, consent }));
      } else {
        await dispatch.handleErrorDispatch(null);
      }
      setLoading(false);
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
      setLoading(false);
    }
  };

  const visible =
    user &&
    user.roles.includes('user') &&
    user.consent?.value &&
    Date.parse(user.consent.accepted as string) <
      Date.parse(POLICIES_UPDATED) &&
    (!user.consent.changesSeen ||
      Date.parse(user.consent.changesSeen as string) <
        Date.parse(POLICIES_UPDATED));

  return visible ? (
    <ModalPortal>
      <Modal
        onHide={confirm}
        title={language === 'fi-FI' ? 'Käytäntöjä päivitetty' : 'Terms updated'}
        visible={true}
      >
        <ModalContent>
          <div className="content">
            <div className="date">
              <DisplayFormattedDate date={POLICIES_UPDATED} />
            </div>
            {language === 'fi-FI' ? (
              <div className="description">
                <p>
                  Olemme päivittäneet tietosuojakäytäntöämme. Aikaisempi
                  tietosuojakäytäntö koski sekä Vuolearning-oppimisympäristöä
                  että muita Vuolearningin tarjoamia palveluja, mutta nyt
                  oppimisalustan tietosuojakäytäntö on eriytetty omaksi
                  käytännöksi.
                </p>

                <p>
                  Oleellisimmat selkeytykset oppimisalustan
                  tietosuojakäytäntöön:
                </p>
                <ul>
                  <li>
                    Emme käytä oppimisalustan henkilötietoja markkinointiin
                  </li>
                  <li>
                    Kolmannen osapuolen evästeiden käyttö ei koske
                    oppimisalustan käyttöä
                  </li>
                </ul>

                <p>Löydät päivitetyn tietosuojakäytännön täältä:</p>
              </div>
            ) : (
              <div className="description">
                <p>
                  We have updated our privacy policy. The previous policy
                  covered both the Vuolearning learning platform and other
                  services provided by Vuolearning, but now the learning
                  platform policy has been separated into its own policy.
                </p>

                <p>
                  The most important clarifications to the updated privacy
                  policy:
                </p>
                <ul>
                  <li>
                    We do not use any personal data on the learning platform for
                    marketing purposes
                  </li>
                  <li>
                    Use of third party cookies does not concern the use of the
                    learning platform
                  </li>
                </ul>

                <p>You can find the updated privacy policy here:</p>
              </div>
            )}
            <Link
              external={true}
              href={
                language === 'fi-FI'
                  ? 'https://www.vuolearning.com/fi/platform-privacy-policy'
                  : 'https://www.vuolearning.com/en/platform-privacy-policy'
              }
              openInNewTab={true}
              text={messages.consentPrivacyPolicy}
            />

            {serverConfig.termsUpdatedCustomInfo ? (
              <div className="custom-content">
                <div
                  dangerouslySetInnerHTML={{
                    __html: serverConfig.termsUpdatedCustomInfo
                  }}
                />
              </div>
            ) : null}

            <style jsx>{`
              .description {
                margin-top: 5px;
              }
              .description ul {
                padding-left: 20px;
              }
              .links {
                margin-top: 10px;
              }
              .custom-content {
                margin-top: 15px;
              }
            `}</style>
          </div>
        </ModalContent>

        <ModalFooter>
          <Button
            category="light"
            loading={loading}
            onClick={confirm}
            text={messages.commonClose}
          />
        </ModalFooter>
      </Modal>
    </ModalPortal>
  ) : null;
};

export default UserConsentUpdate;
